/**
 * POSelectStep — Step 1 of POTransactionsStepper
 * Search and select a Purchase Order; autofills vendor, PO#, PO date, line items.
 */
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Box, Typography, TextField, InputAdornment, Table, TableHead,
  TableBody, TableRow, TableCell, Chip, CircularProgress, alpha, useTheme
} from '@mui/material';
import { Search as SearchIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const STATUS_COLORS = { draft: 'default', sent: 'info', confirmed: 'success', cancelled: 'error' };

export default function POSelectStep({ companyId, selectedPO, onSelect }) {
  const theme = useTheme();
  const [pos, setPOs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('vendor_PO')
        .select('*, suppliers(id, name, email), vendor_PO_items(*)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      setPOs(data || []);
      setLoading(false);
    };
    load();
  }, [companyId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return pos;
    const q = search.toLowerCase();
    return pos.filter(po =>
      po.vendor_PO_number?.toLowerCase().includes(q) ||
      po.suppliers?.name?.toLowerCase().includes(q)
    );
  }, [pos, search]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a Purchase Order to create a transaction. Vendor, PO number, dates and quantities will be pre-filled.
      </Typography>

      <TextField
        fullWidth size="small"
        placeholder="Search by PO number or supplier..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          sx: { borderRadius: 2, bgcolor: 'white' }
        }}
        sx={{ mb: 2 }}
      />

      <Box sx={{ maxHeight: 420, overflowY: 'auto', border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['PO Number', 'Supplier', 'PO Date', 'Expected Delivery', 'Items', 'Status'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 500, fontSize: '0.8rem', bgcolor: alpha(theme.palette.grey[100], 0.9) }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(po => {
              const isSelected = selectedPO?.id === po.id;
              return (
                <TableRow
                  key={po.id}
                  hover
                  selected={isSelected}
                  onClick={() => onSelect(po)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : undefined,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isSelected && <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                      <Typography variant="body2" sx={{ fontWeight: isSelected ? 500 : 400, color: isSelected ? 'primary.main' : 'inherit' }}>
                        {po.vendor_PO_number}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{po.suppliers?.name || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {po.vendor_PO_date ? new Date(po.vendor_PO_date).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {po.expected_delivery_date ? new Date(po.expected_delivery_date).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${po.vendor_PO_items?.length || 0} item${(po.vendor_PO_items?.length || 0) !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={po.status?.toUpperCase() || 'DRAFT'}
                      size="small"
                      color={STATUS_COLORS[po.status] || 'default'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No purchase orders found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {selectedPO && (
        <Box sx={{
          mt: 2, p: 2, borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main', mb: 0.5 }}>
            Selected: {selectedPO.vendor_PO_number}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedPO.suppliers?.name} · {selectedPO.vendor_PO_items?.length || 0} line item{(selectedPO.vendor_PO_items?.length || 0) !== 1 ? 's' : ''}
            {selectedPO.expected_delivery_date ? ` · Expected ${new Date(selectedPO.expected_delivery_date).toLocaleDateString()}` : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
